import { uniqueId } from 'lodash'
import { ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef } from 'react'
import { Position } from 'types'
import { Renderable } from '../../classes/renderer'
import { ArtboardContext } from '../../context'
import { useShader } from '../../hooks/use-shader'
import { ArtboardLayerProps } from '../../types'
import DraggableLayer from './draggable'

const ArtboardLayer = (
    {
        className,
        draggable = false,
        height,
        persistFocus,
        rotation = 0,
        timing = { start: 0, end: 1 },
        width,
        x = 0,
        y = 0,
        zIndex,
        onContextMenu,
        onDoubleClick,
        onDraw,
        onEllipsisMenu,
        onFocusChange,
        onPositionUpdate,
    }: ArtboardLayerProps,
    ref: ForwardedRef<Renderable>
) => {
    const idRef = useRef<string>(uniqueId('artboard-layer'))
    const positionRef = useRef<Position>({ height, rotation, width, x, y })
    const { renderer } = useContext(ArtboardContext)
    const shader = useShader(renderer)

    function onDrag(position: Position) {
        positionRef.current = position
        onPositionUpdate?.(positionRef.current, true)
    }

    function onDragEnd() {
        onPositionUpdate?.(positionRef.current, false)
    }

    useImperativeHandle(ref, () => ({
        id: idRef.current,
        zIndex,
        draw: (progress: number) => {
            const hidden = progress < timing.start || progress > timing.end

            if (shader && !hidden) {
                shader.setProgress(progress)

                onDraw?.(positionRef.current, shader)
            }
        },
    }))

    useEffect(() => {
        // update position if non-draggable layer
        if (!draggable) {
            positionRef.current = {
                height,
                rotation,
                width,
                x,
                y,
            }

            onPositionUpdate?.(positionRef.current, false)
        }
    }, [draggable, height, rotation, width, x, y])

    if (!draggable) {
        return null
    }

    return (
        <DraggableLayer
            className={className}
            defaultTransform={{ rotation, scaleX: 1, scaleY: 1, x, y }}
            height={height}
            persistFocus={persistFocus}
            width={width}
            onContextMenu={onContextMenu}
            onDoubleClick={onDoubleClick}
            onDrag={onDrag}
            onDragEnd={onDragEnd}
            onEllipsisMenu={onEllipsisMenu}
            onFocusChange={onFocusChange}
        />
    )
}

export default forwardRef(ArtboardLayer)
